import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, ChevronDown, Eye, Phone, Copy, Truck, Filter, Download,
  RefreshCw, User, FileText, MessageCircle, X, Calendar, Tag, CreditCard, Layout, CalendarDays
} from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { ordersApi, leadAPIApi, productsApi, orderCampaignApi, employeesApi, courierCredentialApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import ThemeSelect from '../components/ThemeSelect';
import Pagination from '../components/Pagination';
import ExcelExportButton from '../components/ExcelExportButton';
import { message, DatePicker } from 'antd';
import dayjs from 'dayjs';

import { formatDate } from '../utils/helpers';
import { usePermissions } from '../hooks/Usepermissions';

const { RangePicker } = DatePicker;

// Auth Helper Functions
const getAuthUser = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      console.error('Error parsing user:', e);
      return null;
    }
  }
  return null;
};

// Courier tracking URLs
const COURIER_TRACKING_URLS = {
  'E-kart': 'https://ekartlogistics.com/track/',
  'Fship': 'https://www.fship.in/track/',
  'Shadowfax': 'https://tracker.shadowfax.in/#/',
  'Delhivery': 'https://www.delhivery.com/track/package/'
};

// RTO Status Badge Component
const RTOStatusBadge = ({ status }) => {
  const getStatusStyle = (status) => {
    switch (status) {
      case 'RTO Delivered':
        return 'bg-green-50 text-green-600 border-green-200';
      case 'RTO In-transit':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'RTO Initiated':
        return 'bg-orange-50 text-orange-600 border-orange-200';
      case 'RTO Pending':
        return 'bg-yellow-50 text-yellow-600 border-yellow-200';
      case 'REJECTED':
        return 'bg-red-50 text-red-600 border-red-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  return (
    <span className={`px-3 py-1.5 rounded-lg text-xs font-medium border whitespace-nowrap ${getStatusStyle(status)}`}>
      {status || 'RTO Initiated'}
    </span>
  );
};

function RTO() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const { has }      = usePermissions();
  const navigate      = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Date range state (same pattern as OrdersList) ──────────────────────
  const [selectedDateRange, setSelectedDateRange] = useState('allOrders');
  const [selectedDateRangeLabel, setSelectedDateRangeLabel] = useState('All View');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const dateDropdownRef = useRef(null);

  // Refs to avoid stale closures in callbacks
  const selectedDateRangeRef = useRef('allOrders');
  const customStartDateRef   = useRef('');
  const customEndDateRef     = useRef('');
  const activeFiltersRef     = useRef(null);

  const [activeFilters, setActiveFilters] = useState({
    search: '',
    orders: 'All Orders',
    courier: 'All courier',
    courierPartner: 'All Partners',
    dateType: 'Select Type',
    agent: 'All Agents',
    teamLead: 'Select Lead',
    category: 'All Categories',
    status: 'All status',
    paymentMode: 'All Modes',
    campaign: 'All Campaigns'
  });

  const [tempFilters, setTempFilters] = useState(activeFilters);
  const [filterOptions, setFilterOptions] = useState({
    employees: [],
    teamLeads: [],
    campaigns: [],
    categories: [],
    couriers: [],
  });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [ordersPerPage, setOrdersPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const tableContainerRef = useRef(null);

  const statusFilterOptions = ['All status', 'RETURNED', 'RTO'];
  const ordersPerPageOptions = [10, 20, 50, 100];
  // Courier options: API data with static fallback
  const courierOptions = ['All courier', ...(filterOptions.couriers.length > 0 ? filterOptions.couriers : ['E-kart', 'Fship', 'Shadowfax', 'Delhivery'])];
  const partnerOptions = ['All Partners', ...(filterOptions.couriers.length > 0 ? filterOptions.couriers : ['E-kart', 'Fship', 'Shadowfax', 'Delhivery'])];

  const dateRangeOptions = [
    { value: 'allOrders',  label: 'All View' },
    { value: 'today',      label: 'Today' },
    { value: 'yesterday',  label: 'Yesterday' },
    { value: 'this_week',  label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'this_year',  label: 'This Year' },
    { value: 'custom',     label: 'Custom Range' },
  ];

  // ── Keep refs in sync ──────────────────────────────────────────────────
  useEffect(() => { activeFiltersRef.current     = activeFilters;     }, [activeFilters]);
  useEffect(() => { selectedDateRangeRef.current = selectedDateRange; }, [selectedDateRange]);
  useEffect(() => { customStartDateRef.current   = customStartDate;   }, [customStartDate]);
  useEffect(() => { customEndDateRef.current     = customEndDate;     }, [customEndDate]);

  // ── Click outside to close date dropdown ──────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
        setIsDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Load user ──────────────────────────────────────────────────────────
  useEffect(() => {
    const user = getAuthUser();
    setCurrentUser(user);
    setUserLoaded(true);
  }, []);

  // ── Fetch all filter dropdown data from API ───────────────────────────
  const fetchFilterOptions = async () => {
    setFilterOptionsLoading(true);
    try {
      const [empRes, campRes, catRes, courierRes] = await Promise.allSettled([
        employeesApi.getAllEmployees(),
        orderCampaignApi.getAllCampaigns(),
        productsApi.getAllCategories(),
        courierCredentialApi.getAllCredentials(),
      ]);

      // Employees
      const empRaw = empRes.status === 'fulfilled' ? empRes.value : [];
      const empArr = Array.isArray(empRaw) ? empRaw
        : Array.isArray(empRaw?.data) ? empRaw.data
        : Array.isArray(empRaw?.content) ? empRaw.content : [];
      const employees = empArr.map(e => ({
        name: e.name || e.employeeName || e.fullName || '',
        id: e.id,
        teamLeadName: e.teamLeadName || e.teamLead || '',
        teamId: e.teamId,
      })).filter(e => e.name);

      // Unique team leads from employee list
      const teamLeads = [...new Set(employees.map(e => e.teamLeadName).filter(Boolean))].sort();

      // Campaigns
      const campRaw = campRes.status === 'fulfilled' ? campRes.value : [];
      const campArr = Array.isArray(campRaw) ? campRaw
        : Array.isArray(campRaw?.data) ? campRaw.data
        : Array.isArray(campRaw?.content) ? campRaw.content : [];
      const campaigns = [...new Set(campArr.map(c => c.campaignName || c.name || c).filter(Boolean))].sort();

      // Categories
      const catRaw = catRes.status === 'fulfilled' ? catRes.value : [];
      const catArr = Array.isArray(catRaw) ? catRaw
        : Array.isArray(catRaw?.data) ? catRaw.data : [];
      const categories = [...new Set(catArr.map(c => typeof c === 'string' ? c : c?.categoryName || c?.name).filter(Boolean))].sort();

      // Couriers
      const courierRaw = courierRes.status === 'fulfilled' ? courierRes.value : [];
      const courierArr = Array.isArray(courierRaw) ? courierRaw
        : Array.isArray(courierRaw?.data) ? courierRaw.data : [];
      const couriers = [...new Set(courierArr.map(c => c.courierName || c.courierType || c.name || c.type).filter(Boolean))].sort();

      setFilterOptions({ employees, teamLeads, campaigns, categories, couriers });
    } catch (err) {
      console.error('Error loading filter options:', err);
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // ── Sync tempFilters when modal opens ─────────────────────────────────
  useEffect(() => {
    if (isFilterModalOpen) {
      setTempFilters(activeFilters);
    }
  }, [isFilterModalOpen, activeFilters]);

  // ── Initial fetch ──────────────────────────────────────────────────────
  useEffect(() => {
    if (userLoaded) {
      fetchRTOOrders();
    }
  }, [userLoaded]);

  // ── Date resolve (same as OrdersList) ─────────────────────────────────
  const resolveDateRange = (preset, customStart = '', customEnd = '') => {
    const now = new Date();
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const fmtStart = (d) => { d.setHours(0, 0, 0, 0);       return fmt(d); };
    const fmtEnd   = (d) => { d.setHours(23, 59, 59, 999);   return fmt(d); };

    switch (preset) {
      case 'today':
        return { startDate: fmtStart(new Date(now)), endDate: fmtEnd(new Date(now)) };
      case 'yesterday': {
        const y = new Date(now); y.setDate(y.getDate() - 1);
        return { startDate: fmtStart(y), endDate: fmtEnd(new Date(y)) };
      }
      case 'this_week': {
        const d = now.getDay(), diff = now.getDate() - d + (d === 0 ? -6 : 1);
        const start = new Date(now); start.setDate(diff);
        return { startDate: fmtStart(start), endDate: fmtEnd(new Date(now)) };
      }
      case 'this_month':
        return {
          startDate: fmtStart(new Date(now.getFullYear(), now.getMonth(), 1)),
          endDate:   fmtEnd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
        };
      case 'this_year':
        return {
          startDate: fmtStart(new Date(now.getFullYear(), 0, 1)),
          endDate:   fmtEnd(new Date(now.getFullYear(), 11, 31)),
        };
      case 'custom':
        return { startDate: customStart || '', endDate: customEnd || '' };
      default:
        return { startDate: '', endDate: '' };
    }
  };

  // ── Date pill handlers ─────────────────────────────────────────────────
  const handleDateRangeFilter = (rangeType, label) => {
    if (rangeType === 'custom') {
      setIsDateDropdownOpen(false);
      setShowCustomDateModal(true);
      return;
    }
    setSelectedDateRange(rangeType);
    selectedDateRangeRef.current = rangeType;
    setSelectedDateRangeLabel(label || 'All View');
    setIsDateDropdownOpen(false);
    setCurrentPage(1);
    const { startDate, endDate } = resolveDateRange(rangeType);
    fetchRTOOrders({
      overrideDateRange: rangeType,
      overrideStart: startDate,
      overrideEnd: endDate,
      overridePage: 1
    });
  };

  const handleCustomDateApply = () => {
    if (!customStartDate || !customEndDate) { alert('Please select both dates.'); return; }
    if (customEndDate < customStartDate)    { alert('End date must be >= start date.'); return; }
    const lbl = `${new Date(customStartDate).toLocaleDateString('en-GB')} - ${new Date(customEndDate).toLocaleDateString('en-GB')}`;
    setSelectedDateRange('custom');
    selectedDateRangeRef.current = 'custom';
    setSelectedDateRangeLabel(lbl);
    setCurrentPage(1);
    setShowCustomDateModal(false);
    fetchRTOOrders({
      overrideDateRange: 'custom',
      overrideStart: customStartDate,
      overrideEnd: customEndDate,
      overridePage: 1
    });
  };

  // ── MAIN FETCH ─────────────────────────────────────────────────────────
  const fetchRTOOrders = async ({
    overrideDateRange,
    overrideStart,
    overrideEnd,
    overridePage,
    overrideFilters
  } = {}) => {
    try {
      setLoading(true);

      const user          = getAuthUser();
      const filters       = overrideFilters  !== undefined ? overrideFilters  : activeFiltersRef.current || activeFilters;
      const page          = overridePage     !== undefined ? overridePage     : currentPage;
      const dateRange     = overrideDateRange !== undefined ? overrideDateRange : selectedDateRangeRef.current;
      const startInput    = overrideStart    !== undefined ? overrideStart    : customStartDateRef.current;
      const endInput      = overrideEnd      !== undefined ? overrideEnd      : customEndDateRef.current;

      // Resolve date range to startDate / endDate
      const resolved      = (dateRange && dateRange !== 'allOrders')
        ? resolveDateRange(dateRange, startInput, endInput)
        : { startDate: '', endDate: '' };

      const params = {};

      // Search
      if (filters.search?.trim())     params.searchTerm    = filters.search.trim();

      // Courier / partner
      if (filters.courier && filters.courier !== 'All courier')
                                       params.courier       = filters.courier;
      if (filters.courierPartner && filters.courierPartner !== 'All Partners')
                                       params.courierPartner = filters.courierPartner;

      // Date — prefer date range over single date picker
      if (resolved.startDate && resolved.endDate) {
        params.startDate = resolved.startDate;
        params.endDate   = resolved.endDate;
      }

      // dateType maps the date fields on the backend
      if (filters.dateType && filters.dateType !== 'Select Type') {
                                       params.dateType      = filters.dateType;
      } else if (resolved.startDate || resolved.endDate) {
                                       params.dateType      = 'createdDate';
      }

      // Category / status / payment / campaign
      if (filters.category && filters.category !== 'All Categories')
                                       params.category      = filters.category;
      if (filters.paymentMode && filters.paymentMode !== 'All Modes')
                                       params.paymentMode   = filters.paymentMode;
      if (filters.campaign && filters.campaign !== 'All Campaigns')
                                       params.campaign      = filters.campaign;
      if (filters.status && filters.status !== 'All status')
                                       params.status        = filters.status;

      // Employee / Team Lead
      if (user && user.role !== 'ADMIN') {
        params.employeeId = user.id;
      } else if (filters.agent && filters.agent !== 'All Agents') {
        const emp = filterOptions.employees.find(e => e.name === filters.agent);
        if (emp) params.employeeId = emp.id;
      }

      if (filters.teamLead && filters.teamLead !== 'Select Lead') {
        const lead = filterOptions.employees.find(e => e.teamLeadName === filters.teamLead);
        if (lead) params.teamId = lead.teamId;
      }

      // Pagination (1-based UI → 0-based API)
      params.page = page - 1;
      params.size = ordersPerPage;

      const response = await ordersApi.getRTOOrders(params);

      console.log('RTO API Response:', response);

      let ordersData = [];
      let total = 0;

      if (response?.data) {
        ordersData = response.data;
        total = response.total || response.data.length;
      } else if (response?.content) {
        ordersData = response.content;
        total = response.totalElements || response.content.length;
      } else if (Array.isArray(response)) {
        ordersData = response;
        total = response.length;
      }

      const mappedOrders = ordersData.map((order, index) => ({
        id: order.id || index + 1,
        orderId: order.orderId || `OD${order.id}`,
        orderDate: formatDate(order.orderDate || order.createdAt),
        rtoDeliveredDate: (order.rtoDeliveredDate || order.rtoDeliveryDate)
          ? formatDate(order.rtoDeliveredDate || order.rtoDeliveryDate)
          : '-',
        customerName: order.customerName || order.lead?.customerName || '-',
        phone: order.contactNumber || order.phone || order.lead?.contactNumber || '-',
        whatsappVerified: order.whatsappVerified || order.isWhatsappVerified || false,
        product: order.productName || order.product?.name || order.orderItems?.[0]?.productName || '-',
        category: order.category || order.product?.category || '-',
        amount: order.grandTotal || order.amount || order.restAmount || 0,
        payMode: order.paymentMode || order.payMode || 'COD',
        orderCampaign: order.orderCampaign || order.campaign || order.leadSource || '-',
        cPartner: order.cPartner || order.courierPartner || order.courierName || '-',
        courier: order.courierName || order.courier || '-',
        awbNumber: order.trackingNumber || order.awbNumber || '-',
        status: order.rtoStatus || order.status || 'RTO Initiated',
        orderBy: order.orderBy || order.createdByName || '-',
        orderByEmp: order.orderByEmpId || order.employeeId || '-',
        teamLead: order.teamLead || order.teamLeadName || '-',
        teamLeadEmp: order.teamLeadEmpId || '-',
        remarks: order.remarks || '-',
      }));

      setOrders(mappedOrders);
      setTotalOrders(total);

    } catch (error) {
      console.error('Error fetching RTO orders:', error);
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Mask AWB number - show only last 4 digits
  const maskAWBNumber = (awbNumber) => {
    if (!awbNumber || awbNumber === '-' || awbNumber.length <= 4) return awbNumber;
    return 'xxxx' + awbNumber.slice(-4);
  };

  // Navigate to order details
  const handleViewOrder = (orderId) => {
    navigate(`/orders/amount-decrease/${orderId}`, { state: { from: location.pathname } });
  };

  // Refresh
  const handleRefresh = () => {
    fetchRTOOrders();
  };

  // ── Apply filters from modal ───────────────────────────────────────────
  const handleApplyFilters = () => {
    setActiveFilters(tempFilters);
    activeFiltersRef.current = tempFilters;
    setCurrentPage(1);
    setIsFilterModalOpen(false);
    fetchRTOOrders({ overrideFilters: tempFilters, overridePage: 1 });
  };

  // ── Reset filters ──────────────────────────────────────────────────────
  const resetFilters = {
    search: '',
    orders: 'All Orders',
    courier: 'All courier',
    courierPartner: 'All Partners',
    dateType: 'Select Type',
    agent: 'All Agents',
    teamLead: 'Select Lead',
    category: 'All Categories',
    status: 'All status',
    paymentMode: 'All Modes',
    campaign: 'All Campaigns'
  };

  // ── Export ─────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (orders.length === 0) {
      message.warning({ content: 'No data available to export', style: { marginTop: '10vh' } });
      return;
    }
    try {
      const headers = [
        'Order ID', 'Order Date', 'RTO Delivered Date', 'Customer Name', 'Phone',
        'Product', 'Category', 'Amount', 'Pay Mode', 'Campaign',
        'Courier', 'AWB Number', 'Status', 'Order By', 'Team Lead', 'Remarks'
      ];
      const rows = orders.map(order => [
        order.orderId,
        `"${order.orderDate}"`,
        `"${order.rtoDeliveredDate}"`,
        `"${order.customerName}"`,
        order.phone,
        `"${order.product}"`,
        order.category,
        order.amount,
        order.payMode,
        `"${order.orderCampaign}"`,
        order.courier,
        order.awbNumber,
        `"${order.status}"`,
        `"${order.orderBy}"`,
        `"${order.teamLead}"`,
        `"${order.remarks?.replace(/\n/g, ' ')}"`
      ]);
      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `RTO_Orders_${new Date().toLocaleDateString()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success({ content: 'RTO report downloaded successfully!', style: { marginTop: '10vh' } });
    } catch (error) {
      message.error('Failed to export RTO report');
      console.error(error);
    }
  };

  // ── Pagination change ──────────────────────────────────────────────────
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchRTOOrders({ overridePage: page });
  };

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      <div className="flex flex-col gap-6">

      {/* ── Filter Modal ── */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl w-full max-w-4xl mx-4 overflow-hidden flex flex-col shadow-xl max-h-[90vh]">
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4 border-b border-gray-200"
              style={{ background: currentTheme.color }}
            >
              <h2 className="text-lg font-bold text-white">Filter Orders</h2>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Filter Content */}
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

                {/* Courier — from API */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Courier</label>
                  <ThemeSelect
                    value={tempFilters.courier}
                    onChange={(val) => setTempFilters({ ...tempFilters, courier: val })}
                    options={[
                      'All courier',
                      ...(filterOptions.couriers.length > 0
                        ? filterOptions.couriers
                        : ['E-kart', 'Fship', 'Shadowfax', 'Delhivery'])
                    ]}
                    className="w-full"
                  />
                </div>

                {/* Date Type */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Type</label>
                  <ThemeSelect
                    value={tempFilters.dateType}
                    onChange={(val) => setTempFilters({ ...tempFilters, dateType: val })}
                    options={['Select Type', 'Order Date', 'RTO Delivered Date']}
                    className="w-full"
                  />
                </div>

                {/* Agents — from API */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agents
                    {filterOptionsLoading && (
                      <span className="ml-2 inline-block w-3 h-3 border border-t-transparent rounded-full animate-spin align-middle"
                        style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }} />
                    )}
                  </label>
                  <ThemeSelect
                    value={tempFilters.agent}
                    onChange={(val) => setTempFilters({ ...tempFilters, agent: val })}
                    options={['All Agents', ...filterOptions.employees.map(e => e.name)]}
                    className="w-full"
                  />
                </div>

                {/* Team Lead — from API */}
                {/* <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Lead
                    {filterOptionsLoading && (
                      <span className="ml-2 inline-block w-3 h-3 border border-t-transparent rounded-full animate-spin align-middle"
                        style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }} />
                    )}
                  </label>
                  <ThemeSelect
                    value={tempFilters.teamLead}
                    onChange={(val) => setTempFilters({ ...tempFilters, teamLead: val })}
                    options={['Select Lead', ...filterOptions.teamLeads]}
                    className="w-full"
                  />
                </div> */}

                {/* Category — from API */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                    {filterOptionsLoading && (
                      <span className="ml-2 inline-block w-3 h-3 border border-t-transparent rounded-full animate-spin align-middle"
                        style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }} />
                    )}
                  </label>
                  <ThemeSelect
                    value={tempFilters.category}
                    onChange={(val) => setTempFilters({ ...tempFilters, category: val })}
                    options={['All Categories', ...filterOptions.categories]}
                    className="w-full"
                  />
                </div>

                {/* Status */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <ThemeSelect
                    value={tempFilters.status}
                    onChange={(val) => setTempFilters({ ...tempFilters, status: val })}
                    options={statusFilterOptions}
                    className="w-full"
                  />
                </div>

                {/* Payment Mode */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                  <ThemeSelect
                    value={tempFilters.paymentMode}
                    onChange={(val) => setTempFilters({ ...tempFilters, paymentMode: val })}
                    options={['All Modes', 'COD', 'PREPAID']}
                    className="w-full"
                  />
                </div>

                {/* Campaign — from API */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign
                    {filterOptionsLoading && (
                      <span className="ml-2 inline-block w-3 h-3 border border-t-transparent rounded-full animate-spin align-middle"
                        style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }} />
                    )}
                  </label>
                  <ThemeSelect
                    value={tempFilters.campaign}
                    onChange={(val) => setTempFilters({ ...tempFilters, campaign: val })}
                    options={['All Campaigns', ...filterOptions.campaigns]}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setTempFilters(resetFilters)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom Date Modal ── */}
      {showCustomDateModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCustomDateModal(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-scaleIn">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Select Custom Date Range</h3>
              <button
                onClick={() => setShowCustomDateModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date – End Date</label>
              <RangePicker
                value={
                  customStartDate && customEndDate
                    ? [dayjs(customStartDate), dayjs(customEndDate)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setCustomStartDate(dates[0].format('YYYY-MM-DD'));
                    setCustomEndDate(dates[1].format('YYYY-MM-DD'));
                  } else {
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }
                }}
                format="DD-MM-YYYY"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '10px' }}
                className="w-full text-base"
                size="large"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCustomDateModal(false)}
                className="px-5 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
                className="px-5 py-2 text-sm font-semibold text-white rounded-xl transition-colors hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-5">

        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>RTO</h1>
            <span className="text-gray-500 text-lg desktop:text-2xl">({totalOrders.toLocaleString()})</span>

            {/* Date Range Pill — same as OrdersList */}
            <div className="relative" ref={dateDropdownRef}>
              <button
                onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                className="flex items-center bg-white border border-gray-300 rounded-full px-4 py-1.5 shadow-sm cursor-pointer transition-all"
                style={{ borderColor: isDateDropdownOpen ? currentTheme.color : undefined }}
              >
                <CalendarDays className="w-5 h-5 text-orange-400 mr-2" />
                <span className="text-[15px] font-medium text-gray-400">{selectedDateRangeLabel}</span>
                <ChevronDown
                  className={`w-4 h-4 ml-2 transition-transform ${isDateDropdownOpen ? 'rotate-180' : ''}`}
                  style={{ color: currentTheme.color }}
                />
              </button>

              {isDateDropdownOpen && (
                <div className="absolute left-0 top-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 py-3 z-50 w-56">
                  {dateRangeOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => handleDateRangeFilter(opt.value, opt.label)}
                      className="w-full px-5 py-2.5 text-left text-[14px] transition-colors"
                      style={{
                        backgroundColor: selectedDateRange === opt.value ? `${currentTheme.color}15` : 'transparent',
                        color: selectedDateRange === opt.value ? currentTheme.color : '#374151',
                        fontWeight: selectedDateRange === opt.value ? 'bold' : 'normal'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedDateRange !== opt.value)
                          e.currentTarget.style.backgroundColor = `${currentTheme.color}10`;
                      }}
                      onMouseLeave={(e) => {
                        if (selectedDateRange !== opt.value)
                          e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center space-x-3">
            {currentUser && (
              <div className={`flex items-center space-x-2 border rounded-lg px-3 py-2 desktop:px-4 desktop:py-3 ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
                <div className={`w-8 h-8 desktop:w-10 desktop:h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-teal-100'}`}>
                  <User className={`w-4 h-4 desktop:w-6 desktop:h-6 ${isDark ? 'text-gray-400' : 'text-teal-600'}`} />
                </div>
                <div className="text-sm desktop:text-base">
                  <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{currentUser.name}</p>
                  <p className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{currentUser.role}</p>
                </div>
              </div>
            )}
            <button
              onClick={handleRefresh}
              className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/5' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
            >
              <RefreshCw className={`w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/5' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
            >
              <Filter className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500" />
            </button>
            <ExcelExportButton onClick={handleExport} isDownloading={loading} />
          </div>
        </div>

        {/* ── Quick Filters Bar ── */}
        <div className="flex items-center space-x-4 mb-6 flex-wrap gap-y-3">
          {/* Search */}
          <ExpandableSearchBar
            searchTerm={activeFilters.search}
            onSearchChange={(value) => {
              const updated = { ...activeFilters, search: value };
              setActiveFilters(updated);
              activeFiltersRef.current = updated;
              setCurrentPage(1);
              fetchRTOOrders({ overrideFilters: updated, overridePage: 1 });
            }}
            placeholder="Search by order id or customer name"
          />

          {/* Orders per page */}
          <ThemeSelect
            value={`${ordersPerPage} Orders`}
            onChange={(val) => {
              const n = Number(parseInt(val));
              setOrdersPerPage(n);
              setCurrentPage(1);
              fetchRTOOrders({ overridePage: 1 });
            }}
            options={ordersPerPageOptions.map(opt => `${opt} Orders`)}
            className="w-44 desktop:w-60"
          />

          {/* Courier Filter */}
          <ThemeSelect
            value={activeFilters.courier}
            onChange={(val) => {
              const updated = { ...activeFilters, courier: val };
              setActiveFilters(updated);
              activeFiltersRef.current = updated;
              setCurrentPage(1);
              fetchRTOOrders({ overrideFilters: updated, overridePage: 1 });
            }}
            options={courierOptions}
            icon={Truck}
            className="w-48 desktop:w-64"
          />

          {/* Partner Filter */}
          <ThemeSelect
            value={activeFilters.courierPartner}
            onChange={(val) => {
              const updated = { ...activeFilters, courierPartner: val };
              setActiveFilters(updated);
              activeFiltersRef.current = updated;
              setCurrentPage(1);
              fetchRTOOrders({ overrideFilters: updated, overridePage: 1 });
            }}
            options={partnerOptions}
            icon={User}
            className="w-48 desktop:w-64"
          />

          {/* Status Filter */}
          <ThemeSelect
            value={activeFilters.status}
            onChange={(val) => {
              const updated = { ...activeFilters, status: val };
              setActiveFilters(updated);
              activeFiltersRef.current = updated;
              setCurrentPage(1);
              fetchRTOOrders({ overrideFilters: updated, overridePage: 1 });
            }}
            options={statusFilterOptions}
            icon={RefreshCw}
            className="w-48 desktop:w-64"
          />

          {/* Clear all filters button */}
          {(activeFilters.search ||
            activeFilters.courier !== 'All courier' ||
            activeFilters.courierPartner !== 'All Partners' ||
            activeFilters.status !== 'All status' ||
            activeFilters.category !== 'All Categories' ||
            activeFilters.paymentMode !== 'All Modes' ||
            activeFilters.campaign !== 'All Campaigns' ||
            activeFilters.agent !== 'All Agents' ||
            activeFilters.teamLead !== 'Select Lead' ||
            selectedDateRange !== 'allOrders') && (
            <button
              onClick={() => {
                setActiveFilters(resetFilters);
                activeFiltersRef.current = resetFilters;
                setTempFilters(resetFilters);
                setCurrentPage(1);
                setSelectedDateRange('allOrders');
                selectedDateRangeRef.current = 'allOrders';
                setSelectedDateRangeLabel('All View');
                setCustomStartDate('');
                setCustomEndDate('');
                customStartDateRef.current = '';
                customEndDateRef.current   = '';
                fetchRTOOrders({
                  overrideFilters: resetFilters,
                  overrideDateRange: 'allOrders',
                  overrideStart: '',
                  overrideEnd: '',
                  overridePage: 1
                });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* ── Table ── */}
        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
          <div ref={tableContainerRef} className="overflow-x-auto">
            <table className="w-full min-w-[1800px]">
              <thead className="text-white" style={{ background: currentTheme.color }}>
                <tr>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Order ID</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Order Date</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">RTO Delivered Date</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Name</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Product</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Category</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Amount</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Pay Mode</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Order Campaign</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">C.Partner</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Courier</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">AWB No.</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Order By</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Team Lead</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Remarks</th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                {loading ? (
                  <tr>
                    <td colSpan={17} className="px-4 py-12 text-center">
                      <div className="flex items-center justify-center space-x-3">
                        <div
                          className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }}
                        />
                        <span className="text-gray-500">Loading RTO orders...</span>
                      </div>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={17} className="px-4 py-12 text-center text-gray-500">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p>No RTO orders found</p>
                    </td>
                  </tr>
                ) : (
                  orders.map((order, idx) => (
                    <tr
                      key={order.id}
                      className={`transition-colors ${isDark ? (idx % 2 === 0 ? 'bg-[#111827]' : 'bg-white/[0.02]') + ' hover:bg-white/5' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50') + ' hover:bg-gray-50'}`}
                    >
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.orderId}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className="text-sm desktop:text-base text-gray-600 whitespace-nowrap">{order.orderDate}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className="text-sm desktop:text-base text-gray-600 whitespace-nowrap">{order.rtoDeliveredDate || '-'}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.customerName}</span>
                          <div className="flex items-center space-x-1.5 mt-0.5">
                            <Phone className="w-3.5 h-3.5 desktop:w-4 desktop:h-4 text-gray-500" />
                            <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{order.phone}</span>
                            {order.whatsappVerified && (
                              <div className="w-4 h-4 desktop:w-5 desktop:h-5 bg-green-500 rounded-full flex items-center justify-center">
                                <MessageCircle className="w-2.5 h-2.5 desktop:w-3 desktop:h-3 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate block max-w-[150px]`} title={order.product}>
                          {order.product}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{order.category}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className="text-sm desktop:text-base font-semibold" style={{ color: currentTheme.color }}>₹ {order.amount.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.payMode}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.orderCampaign}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.cPartner}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.courier}</span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className={`flex items-center space-x-2 w-fit px-3 py-1.5 rounded-lg transition-all ${isDark ? 'bg-white/5 border border-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'}`}>
                          <span className={`text-sm desktop:text-base font-mono font-bold ${isDark ? 'text-white' : 'text-teal-600'}`}>
                            {maskAWBNumber(order.awbNumber)}
                          </span>
                          {order.awbNumber && order.awbNumber !== '-' && (
                            <button
                              onClick={() => copyToClipboard(order.awbNumber)}
                              className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-white'}`}
                              title="Copy full AWB number"
                            >
                              <Copy className={`w-3.5 h-3.5 desktop:w-4 desktop:h-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <RTOStatusBadge status={order.status} />
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'} truncate max-w-[120px]`}>{order.orderBy}</span>
                          <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {order.orderByEmp}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'} truncate max-w-[120px]`}>{order.teamLead}</span>
                          <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {order.teamLeadEmp}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base ${isDark ? 'text-gray-400' : 'text-gray-700'} block max-w-[200px]`}
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                          title={order.remarks}
                        >
                          {order.remarks}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-center">
                        <button
                          onClick={() => handleViewOrder(order.id)}
                          className={`p-2 desktop:p-3 rounded-xl transition-all ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                          style={{ color: currentTheme.color }}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 desktop:w-5 desktop:h-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination — server-side */}
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalItems={totalOrders}
              itemsPerPage={ordersPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default RTO;